import React, { useEffect, useMemo, useState } from "react";
import client, { apiError } from "@/api/client";
import { useAuth } from "@/context/AuthContext";
import { useBranch } from "@/context/BranchContext";
import { rupiah, dateTimeFmt } from "@/lib/format";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Card } from "@/components/ui/card";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Plus, Trash2, Truck, AlertTriangle, FileText, ArrowRight, Loader2 } from "lucide-react";

export default function Purchasing() {
  const { user } = useAuth();
  const { effectiveBranch, isAllBranch } = useBranch();
  const [products, setProducts] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [history, setHistory] = useState([]);
  const [supplierId, setSupplierId] = useState("");
  const [lines, setLines] = useState([]);
  const [saving, setSaving] = useState(false);
  const [detail, setDetail] = useState(null);

  const branchParam = effectiveBranch ? { branch_id: effectiveBranch } : {};
  const load = () => {
    client.get("/products", { params: branchParam }).then((r) => setProducts(r.data));
    client.get("/suppliers").then((r) => setSuppliers(r.data));
    client.get("/purchases", { params: branchParam }).then((r) => setHistory(r.data));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [effectiveBranch]);

  const addLine = () => setLines((l) => [...l, { _k: Math.random().toString(36).slice(2), product_id: "", unit: "", qty: 1, cost_per_unit: 0 }]);
  const setLine = (i, patch) => setLines((l) => l.map((it, idx) => (idx === i ? { ...it, ...patch } : it)));
  const delLine = (i) => setLines((l) => l.filter((_, idx) => idx !== i));

  const prodUnits = (pid) => products.find((p) => p.id === pid)?.units || [];
  const total = useMemo(() => lines.reduce((s, l) => s + (Number(l.qty) || 0) * (Number(l.cost_per_unit) || 0), 0), [lines]);

  const submit = async () => {
    if (saving) return;
    if (!lines.length) { toast.error("Tambah minimal 1 item"); return; }
    setSaving(true);
    try {
      await client.post("/purchases", {
        branch_id: effectiveBranch, supplier_id: supplierId || null,
        items: lines.map((l) => ({ product_id: l.product_id, unit: l.unit, qty: Number(l.qty), cost_per_unit: Number(l.cost_per_unit) })),
      });
      toast.success("Pembelian tercatat, stok & HPP diperbarui");
      setLines([]); setSupplierId(""); load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  if (isAllBranch && !effectiveBranch) {
    return <div className="flex h-full items-center justify-center p-6 text-center text-muted-foreground"><div><AlertTriangle className="mx-auto mb-2 h-8 w-8 opacity-40" />Pilih cabang untuk mencatat pembelian.</div></div>;
  }

  return (
    <div className="p-6 space-y-6 animate-in-up">
      <div>
        <h1 className="font-heading text-3xl font-bold tracking-tight">Pembelian</h1>
        <p className="text-sm text-muted-foreground">Catat pembelian dari supplier — menambah stok & memperbarui HPP rata-rata</p>
      </div>

      <Card className="p-5">
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="space-y-2"><Label>Supplier</Label>
            <Select value={supplierId} onValueChange={setSupplierId}>
              <SelectTrigger data-testid="purchase-supplier"><SelectValue placeholder="Pilih supplier (opsional)" /></SelectTrigger>
              <SelectContent>{suppliers.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>

        <div className="mt-4 space-y-2">
          <div className="flex items-center justify-between">
            <Label>Item Pembelian</Label>
            <Button size="sm" variant="outline" onClick={addLine} data-testid="add-line-btn"><Plus className="mr-1 h-3 w-3" /> Tambah Item</Button>
          </div>
          {lines.map((l, i) => (
            <div key={l._k} className="grid grid-cols-12 items-center gap-2" data-testid={`purchase-line-${i}`}>
              <div className="col-span-5">
                <Select value={l.product_id} onValueChange={(v) => { const u = products.find((p) => p.id === v)?.base_unit || ""; setLine(i, { product_id: v, unit: u }); }}>
                  <SelectTrigger data-testid={`line-product-${i}`}><SelectValue placeholder="Produk" /></SelectTrigger>
                  <SelectContent>{products.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="col-span-2">
                <Select value={l.unit} onValueChange={(v) => setLine(i, { unit: v })}>
                  <SelectTrigger data-testid={`line-unit-${i}`}><SelectValue placeholder="Satuan" /></SelectTrigger>
                  <SelectContent>{prodUnits(l.product_id).map((u) => <SelectItem key={u.name} value={u.name}>{u.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <Input className="col-span-2 font-mono" type="number" placeholder="Qty" value={l.qty} onChange={(e) => setLine(i, { qty: e.target.value })} data-testid={`line-qty-${i}`} />
              <Input className="col-span-2 font-mono" type="number" placeholder="Harga beli / satuan" value={l.cost_per_unit} onChange={(e) => setLine(i, { cost_per_unit: e.target.value })} data-testid={`line-cost-${i}`} />
              <button className="col-span-1 flex justify-center text-muted-foreground hover:text-destructive" onClick={() => delLine(i)}><Trash2 className="h-4 w-4" /></button>
            </div>
          ))}
          {!lines.length && <div className="rounded-md border border-dashed py-8 text-center text-sm text-muted-foreground">Belum ada item. Klik "Tambah Item".</div>}
        </div>

        <div className="mt-4 flex items-center justify-between border-t pt-4">
          <div className="text-lg font-bold">Total: <span className="font-mono text-primary">{rupiah(total)}</span></div>
          <Button onClick={submit} disabled={saving} data-testid="submit-purchase-btn">
            {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Truck className="mr-2 h-4 w-4" />}
            {saving ? "Menyimpan…" : "Simpan Pembelian"}
          </Button>
        </div>
      </Card>

      <div>
        <h2 className="mb-3 font-heading text-xl font-semibold">Riwayat Pembelian</h2>
        <div className="rounded-lg border bg-white">
          <Table>
            <TableHeader><TableRow><TableHead>No</TableHead><TableHead>Tanggal</TableHead><TableHead>Supplier</TableHead><TableHead>Item</TableHead><TableHead className="text-right">Total</TableHead><TableHead>Oleh</TableHead><TableHead className="text-right">Detail</TableHead></TableRow></TableHeader>
            <TableBody>
              {history.map((h) => (
                <TableRow key={h.id} className="hover:bg-slate-50" data-testid={`purchase-row-${h.id}`}>
                  <TableCell className="font-mono text-xs">{h.number}</TableCell>
                  <TableCell className="text-xs">{dateTimeFmt(h.created_at)}</TableCell>
                  <TableCell className="text-sm">{h.supplier_name || "—"}</TableCell>
                  <TableCell className="text-sm">{h.items.length} item</TableCell>
                  <TableCell className="text-right font-mono">{rupiah(h.total)}</TableCell>
                  <TableCell className="text-sm">{h.user_name}</TableCell>
                  <TableCell className="text-right">
                    <Button variant="ghost" size="sm" onClick={() => setDetail(h)} data-testid={`purchase-detail-${h.id}`}>
                      <FileText className="h-4 w-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
              {!history.length && <TableRow><TableCell colSpan={7} className="py-8 text-center text-muted-foreground">Belum ada pembelian</TableCell></TableRow>}
            </TableBody>
          </Table>
        </div>
      </div>

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-3xl w-[95vw]" data-testid="purchase-detail-dialog">
          <DialogHeader>
            <DialogTitle>Detail Pembelian {detail?.number}</DialogTitle>
            <DialogDescription>
              {detail?.branch_name} · {dateTimeFmt(detail?.created_at)} · oleh {detail?.user_name}
            </DialogDescription>
          </DialogHeader>
          {detail && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3 text-sm sm:grid-cols-3">
                <div><div className="text-xs text-muted-foreground">Supplier</div><div className="font-medium">{detail.supplier_name || "—"}</div></div>
                <div><div className="text-xs text-muted-foreground">Total</div><div className="font-mono font-semibold text-primary">{rupiah(detail.total)}</div></div>
                <div><div className="text-xs text-muted-foreground">Jumlah Item</div><div className="font-medium">{detail.items.length}</div></div>
                {detail.note && <div className="col-span-2 sm:col-span-3"><div className="text-xs text-muted-foreground">Catatan</div><div>{detail.note}</div></div>}
              </div>

              <div className="rounded-lg border overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Produk</TableHead>
                      <TableHead className="text-right">Qty</TableHead>
                      <TableHead className="text-right">Harga Beli/Satuan</TableHead>
                      <TableHead className="text-right">Subtotal</TableHead>
                      <TableHead className="text-center">Perubahan Stok</TableHead>
                      <TableHead className="text-center">Perubahan Harga Beli (HPP)</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {detail.items.map((it, i) => {
                      const hasStock = it.stock_after !== undefined && it.stock_after !== null;
                      const hasAvg = it.avg_after !== undefined && it.avg_after !== null;
                      const baseUnit = products.find((p) => p.id === it.product_id)?.base_unit || "";
                      return (
                        <TableRow key={i} data-testid={`purchase-detail-item-${i}`}>
                          <TableCell className="font-medium">{it.name}</TableCell>
                          <TableCell className="text-right whitespace-nowrap">{it.qty} {it.unit}</TableCell>
                          <TableCell className="text-right font-mono">{rupiah(it.cost_per_unit)}</TableCell>
                          <TableCell className="text-right font-mono">{rupiah(it.subtotal)}</TableCell>
                          <TableCell className="text-center">
                            {hasStock ? (
                              <span className="inline-flex items-center gap-1 font-mono text-xs whitespace-nowrap" data-testid={`purchase-detail-stock-${i}`}>
                                <span className="text-muted-foreground">{it.stock_before}</span>
                                <ArrowRight className="h-3 w-3 text-emerald-600" />
                                <span className="font-semibold text-emerald-700">{it.stock_after}</span>
                                {baseUnit && <span className="text-muted-foreground">{baseUnit}</span>}
                              </span>
                            ) : "—"}
                          </TableCell>
                          <TableCell className="text-center">
                            {hasAvg ? (
                              <span className="inline-flex items-center gap-1 font-mono text-xs whitespace-nowrap" data-testid={`purchase-detail-avg-${i}`}>
                                <span className="text-muted-foreground">{rupiah(Math.round(it.avg_before))}</span>
                                <ArrowRight className="h-3 w-3 text-amber-600" />
                                <span className="font-semibold text-amber-700">{rupiah(Math.round(it.avg_after))}</span>
                                {baseUnit && <span className="text-muted-foreground">/{baseUnit}</span>}
                              </span>
                            ) : "—"}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
              {detail.items.some((it) => it.stock_after === undefined || it.stock_after === null) && (
                <p className="text-xs text-muted-foreground">Catatan: pembelian lama tidak menyimpan rincian perubahan stok/harga, sehingga ditampilkan "—".</p>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
